import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpBackend,
  HttpClient,
} from '@angular/common/http';
import { Observable, switchMap, of, catchError, delay } from 'rxjs';
import { environment } from '../../environments/environment';

/** Simulated network latency in mock mode (ms). */
const MOCK_DELAY_MS = 500;

/**
 * In mock mode, intercepts every outgoing API request and returns the contents
 * of `src/assets/{endpointName}.json` as the HTTP response body.
 *
 * The endpoint name is the last path segment of the request URL, e.g.:
 *   .../core/user/user-access  →  assets/user-access.json
 *
 * Asset fetches (URLs starting with /assets/) are passed through untouched to
 * avoid circular interception of the mock files themselves.
 *
 * Uses HttpBackend directly to bypass the interceptor chain when fetching the
 * asset file, avoiding a circular dependency.
 */
@Injectable()
export class MockInterceptor implements HttpInterceptor {
  private readonly http: HttpClient;

  constructor(handler: HttpBackend) {
    this.http = new HttpClient(handler);
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!environment.mockMode || req.url.startsWith('/assets/mocks/')) {
      return next.handle(req);
    }

    const segments = req.url.split('/').map(s => s.split('?')[0]).filter(Boolean);
    let endpoint = segments[segments.length - 1];
    // If last segment is a dynamic value (email or numeric ID), use the segment before it
    const decoded = decodeURIComponent(endpoint);
    if (decoded.includes('@') || !isNaN(Number(decoded))) {
      endpoint = segments[segments.length - 2];
    }
    if (!endpoint) {
      return next.handle(req);
    }

    return this.http.get(`/assets/mocks/${endpoint}.json`).pipe(
      delay(MOCK_DELAY_MS),
      switchMap((body) => of(new HttpResponse({ status: 200, body }))),
      catchError((err) => {
        console.error(
          `[MockInterceptor] No mock file found for endpoint "${endpoint}". ` +
            `Create src/assets/mocks/${endpoint}.json to mock this request.`,
          err
        );
        throw err;
      })
    );
  }
}
